import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Space, Input, InputNumber, Popconfirm, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import DdosTag from './DdosTag';

const { Text } = Typography;

const HEALTH_COLOR = { up: 'success', down: 'error', unknown: 'default' };

/** 机器的 IP 清单:增删 IP、健康、所在子池(发布只在子池里配置) */
const NodeIpsPanel = ({ nodeId }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newIp, setNewIp] = useState('');
    const [newDdos, setNewDdos] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        if (!nodeId) return;
        setLoading(true);
        const r = await cdnNodeOpsService.listNodeIps(nodeId);
        setLoading(false);
        if (r.success) setItems(r.data?.items || []);
        else message.error(r.error || t('Failed to load'));
    }, [nodeId, t]);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        const ip = newIp.trim();
        if (!ip) return;
        setAdding(true);
        const r = await cdnNodeOpsService.createNodeIp({ node_id: nodeId, ip, ddos_gbps: newDdos });
        setAdding(false);
        if (r.success) { setNewIp(''); setNewDdos(null); load(); } else message.error(r.error || t('Save failed'));
    };

    const saveDdos = async (row, v) => {
        if ((v || null) === (row.ddos_gbps || null)) return;
        const r = await cdnNodeOpsService.updateNodeIp({ id: row.id, ddos_gbps: v || '' });
        if (r.success) load(); else message.error(r.error || t('Save failed'));
    };

    const remove = async (id) => {
        const r = await cdnNodeOpsService.deleteNodeIp(id);
        if (r.success) load(); else message.error(r.error || t('Delete failed'));
    };

    const columns = [
        {
            title: 'IP', dataIndex: 'ip',
            render: (ip, r) => (
                <Space size={4} wrap>
                    <Text code>{ip}</Text>
                    <DdosTag gbps={r.ddos_gbps} />
                    {r.family === 'ipv6' && <Tag>IPv6</Tag>}
                    {r.is_primary && <Tag>{t('Primary')}</Tag>}
                    {r.anycast && <Tag color="purple">Anycast</Tag>}
                    {r.in_dns && <Tag color="blue">DNS</Tag>}
                </Space>
            ),
        },
        {
            title: 'DDoS', dataIndex: 'ddos_gbps', width: 110,
            render: (v, r) => (
                <InputNumber size="small" min={0} max={100000} defaultValue={v} addonAfter="G" style={{ width: 100 }}
                    onBlur={(e) => saveDdos(r, e.target.value === '' ? null : Number(e.target.value))} />
            ),
        },
        {
            title: t('IP pools'), dataIndex: 'pools',
            render: (pools) => (pools?.length
                ? <Space size={[0, 4]} wrap>{pools.map((p) => <Tag key={p.id} color={p.published ? 'purple' : 'default'}>{p.name}</Tag>)}</Space>
                : <Text type="secondary">-</Text>),
        },
        {
            title: t('Health'), dataIndex: 'health', width: 150,
            render: (h, r) => (
                <Tooltip title={r.results?.length
                    ? r.results.map((x) => <div key={x.v}>{x.ok ? '✓' : '✗'} {x.v}{x.x ? ` (${t('ignored')})` : ''} · {x.ok ? `${x.ms}ms` : (x.err || x.code)}</div>)
                    : t('No probe results yet')}>
                    <Space size={4}>
                        <Tag color={HEALTH_COLOR[h] || 'default'}>{t(h === 'up' ? 'Up' : h === 'down' ? 'Down' : 'Unknown')}</Tag>
                        {r.votes > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{r.votes_ok}/{r.votes}</Text>}
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: '', key: 'op', width: 40,
            render: (_, r) => !r.is_primary && !r.anycast && (
                <Popconfirm title={t('Delete this IP?')} onConfirm={() => remove(r.id)}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
                <Input size="small" placeholder={t('Add IP')} value={newIp} onChange={(e) => setNewIp(e.target.value)}
                    onPressEnter={add} style={{ width: 260 }} />
                <InputNumber size="small" min={0} max={100000} placeholder="DDoS" addonAfter="G" value={newDdos} onChange={setNewDdos} style={{ width: 120 }} />
                <Button size="small" icon={<PlusOutlined />} loading={adding} onClick={add} />
                <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
            </Space>
            <Table size="small" rowKey="id" columns={columns} dataSource={items} loading={loading} pagination={false} />
        </Space>
    );
};

export default NodeIpsPanel;
